import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { toast } from 'sonner';
import { Heart, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTEREST_LABELS = {
  olfactif: 'Coaching olfactif',
  emotions: 'Émotions & stress',
  cnv: 'Communication non violente',
  'femme-entrepreneure': 'Femme · Mère · Entrepreneure',
};

const AdminCoachingWaitlist = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetch(); }, []);

  const fetch = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/admin/coaching/waitlist`, { withCredentials: true });
      setList(data);
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = () => {
    if (list.length === 0) return;
    const headers = ['Email', 'Prénom', 'Intérêts', 'Date inscription'];
    const rows = list.map((l) => [
      l.email,
      l.name || '',
      (l.interests || []).map((i) => INTEREST_LABELS[i] || i).join(' | '),
      new Date(l.created_at).toLocaleString('fr-FR'),
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${(c || '').toString().replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `liste-attente-coaching-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const interestStats = list.reduce((acc, l) => {
    (l.interests || []).forEach((i) => { acc[i] = (acc[i] || 0) + 1; });
    return acc;
  }, {});

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl text-[#5C2018]" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
              Liste d'attente Coaching
            </h1>
            <p className="text-sm text-[#332724]/70 mt-1">
              Personnes intéressées par le lancement du coaching Harmony.
            </p>
          </div>
          {list.length > 0 && (
            <button
              onClick={exportCsv}
              className="btn-primary px-6 py-2 rounded-full text-xs uppercase tracking-[0.18em]"
              data-testid="export-csv"
            >
              Exporter CSV
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-[#5C2018] text-[#FDFAF4] p-4 rounded-xl">
            <p className="text-xs uppercase tracking-[0.2em] text-[#B8922A] mb-1">Total inscrits</p>
            <p className="text-3xl" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>{list.length}</p>
          </div>
          {Object.entries(INTEREST_LABELS).map(([key, label]) => (
            <div key={key} className="bg-white p-4 rounded-xl border border-gold">
              <p className="text-xs uppercase tracking-[0.2em] text-[#B8922A] mb-1 truncate">{label}</p>
              <p className="text-2xl text-[#5C2018]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{interestStats[key] || 0}</p>
            </div>
          ))}
        </div>

        {loading ? (
          <p className="text-center py-12">Chargement…</p>
        ) : list.length === 0 ? (
          <div className="bg-white p-12 rounded-2xl border border-gold text-center">
            <Heart className="mx-auto text-[#B8922A]/50 mb-3" size={48} />
            <p className="text-[#332724]/70">Aucune inscription pour le moment.</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gold overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#FDFAF4] border-b border-gold">
                <tr className="text-left">
                  <th className="p-4 text-xs uppercase tracking-wider text-[#B8922A]">Email</th>
                  <th className="p-4 text-xs uppercase tracking-wider text-[#B8922A]">Prénom</th>
                  <th className="p-4 text-xs uppercase tracking-wider text-[#B8922A]">Intérêts</th>
                  <th className="p-4 text-xs uppercase tracking-wider text-[#B8922A]">Inscription</th>
                </tr>
              </thead>
              <tbody>
                {list.map((l) => (
                  <tr key={l.id} className="border-b border-[#B8922A]/15 hover:bg-[#FDFAF4]/50" data-testid={`waitlist-row-${l.id}`}>
                    <td className="p-4">
                      <a href={`mailto:${l.email}`} className="text-[#1E7A6A] hover:underline inline-flex items-center gap-1.5">
                        <Mail size={14} /> {l.email}
                      </a>
                    </td>
                    <td className="p-4 text-[#332724]">{l.name || '—'}</td>
                    <td className="p-4">
                      <div className="flex flex-wrap gap-1">
                        {(l.interests || []).length === 0 ? (
                          <span className="text-[#332724]/40 text-xs italic">aucun précisé</span>
                        ) : (
                          (l.interests || []).map((i) => (
                            <span key={i} className="px-2 py-0.5 rounded-full bg-[#B8922A]/20 text-[#5C2018] text-xs">
                              {INTEREST_LABELS[i] || i}
                            </span>
                          ))
                        )}
                      </div>
                    </td>
                    <td className="p-4 text-[#332724]/70 text-xs">
                      {new Date(l.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminCoachingWaitlist;
